/**
 * @file apps/web/src/components/TemplatePickerModal.tsx
 * @description Small modal used by the per-test-script "Submit for
 * Approval" button — lets the user pick one of the project's approval
 * workflow templates, then creates an approval instantiated from it.
 */

import { useEffect, useState } from 'react';
import { X, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { approvalTemplatesApi, approvalsApi } from '../lib/api.js';

interface Template {
  id:            string;
  name:          string;
  workflow_type: string;
  steps:         { id: string }[];
}

interface TemplatePickerModalProps {
  projectId:  string;
  entityType: string;
  entityId:   string;
  title:      string;
  onCreated:  (approval: unknown) => void;
  onCancel:   () => void;
}

export function TemplatePickerModal({
  projectId, entityType, entityId, title, onCreated, onCancel,
}: TemplatePickerModalProps) {
  const { devHeaders } = useAuth();
  const [templates, setTemplates]   = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr]               = useState('');

  useEffect(() => {
    let cancelled = false;
    approvalTemplatesApi.list(projectId, devHeaders)
      .then((data) => { if (!cancelled) setTemplates(data as Template[]); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!selectedId) { setErr('Select a template.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const created = await approvalsApi.create({
        project_id:  projectId,
        entity_type: entityType,
        entity_id:   entityId,
        template_id: selectedId,
        title,
      }, devHeaders);
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to submit for approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="card p-5 space-y-4 w-full max-w-sm bg-white">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold text-ink">{title}</h3>
          <button onClick={onCancel} className="btn-icon text-muted hover:text-ink">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="h-24 animate-pulse bg-gray/60 rounded-lg" />
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted">
            No approval workflow templates exist for this project yet. Create one in the
            Approvals tab&rsquo;s Setup view first.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={[
                  'w-full text-left p-2.5 rounded-lg border text-sm transition-colors',
                  selectedId === t.id
                    ? 'border-blue bg-sky text-ink'
                    : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20 text-ink',
                ].join(' ')}
              >
                <p className="font-medium">{t.name}</p>
                <p className="text-[11px] font-mono text-muted mt-0.5">
                  {t.workflow_type} · {t.steps.length} approver{t.steps.length === 1 ? '' : 's'}
                </p>
              </button>
            ))}
          </div>
        )}

        {err && (
          <div className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle size={13} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
          <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting || templates.length === 0}
            className="btn-primary text-sm"
          >
            <ShieldCheck size={14} /> {submitting ? 'Submitting…' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}
